/**
 * SessionCard — the START-SESSION PAGE for a host connection, drawn where the
 * dead "no UI — the app performs it" chip used to be.
 *
 * The chip was factually right and practically useless: `host.openCapture` has
 * no UI OF OURS, but it carries the prompt, the hidden model instructions, the
 * capture framing and the session binding — the entire content of that tap.
 * The first replacement printed all of that as a key list, which fixed the
 * chip and not the reading: a scene-studio template arrives with its whole
 * session nested under `args`, so the card's answer to "what does Create scene
 * start?" was a 4 KB JSON blob to parse by eye.
 *
 * It reads TOP-DOWN now, in the order the question is actually asked:
 *
 *   SESSION TYPE   `widget` or `widget_main` — the `Session.session_type` the
 *                  backend will record, which is a property of the SLOT
 *                  (`openMainSession` reaches the one persistent per-instance
 *                  chat; everything else opens or continues a capture session).
 *                  A DELEGATED preflight says so out loud: the tap opens a page
 *                  carrying the session, and the session opens a screen later.
 *   PROMPT         the visible text as text — wrapped, whole, not a preview —
 *                  with each `<vi_prefix>` / `<vi_suffix>` block beside it in
 *                  its own marked lane, because "hidden from the user, sent to
 *                  the model" is the one property of a prompt a reader cannot
 *                  recover from the string.
 *   INJECTIONS     the four switches steering that prompt — `prompt_policy`,
 *                  `template_id`, `image_roles`, `required_media_count`.
 *   IMAGES         the photos that travel with it, IN ORDER, as thumbnails
 *                  under their role names. An empty slot says which of the two
 *                  empties it is: unresolved here, or collected by the sheet.
 *   ALSO SENT      everything else, unfolding, `args` flattened one key per
 *                  row. Nothing the payload carries is dropped — a diagram
 *                  that quietly loses a field is worse than no diagram.
 *
 * ROWS UNFOLD, they never grow the card. Every wire in the strip is anchored to
 * a box clamped to the phone frame (`FocusExplorer.measure`), so a card that
 * got taller on a click would leave every anchor pointing at where the cards
 * used to be. The body scrolls inside the frame instead; the header — session
 * type, ladder, what it resolved against, the option switcher — stays put,
 * because with the body scrolled away it is the only thing that says which
 * session you are reading.
 *
 * THE OPTION SWITCHER exists because focus is per MODULE and a template rail is
 * eleven different payloads behind one node. The card opens on the option the
 * reader actually clicked (the interaction store's `ui[nodeId]`), says so when
 * nothing has been clicked yet, and lets any other be inspected without
 * touching the running app's selection — a diagram must not be an input.
 *
 * NOTHING BASE64 IS EVER PRINTED. A cover is a ~40 KB `data:image/webp`; it is
 * drawn as an image or metered as a size, never rendered as text.
 *
 * Everything is drawn in PHONE pixels (390×844) inside `PhoneCard`'s 0.558
 * scale, so the type here is deliberately ~1.8× what it reads as on screen.
 */
import { useEffect, useMemo, useState } from 'react';
import { isRenderableSrc, isUnresolved } from './sessionModel';

const C = {
  text: '#1c1c1e', dim: '#6e6e73', line: '#e2e2ea', accent: '#6E59F2',
  warn: '#b45309', good: '#1a7a4d', panel: '#ffffff', hidden: '#8a5cf6',
};

const MONO = 'ui-monospace, SFMono-Regular, monospace';

/** What the app does with this payload, in one line per rung. */
const KIND_LABEL = {
  capture: 'opens a CAPTURE session',
  main: 'opens the MAIN chat session',
  session: 'continues the entry’s SESSION',
  delegated: 'hands the next page this session’s preflight',
};

const GROUP_LABEL = {
  prompt: 'prompt',
  flag: 'check',
  capture: 'capture framing',
  binding: 'session binding',
  delegated: 'page target',
  args: 'args',
  extra: 'also sent',
};

/** A section heading — the question the block under it answers. */
function Heading({ children, note = null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, paddingTop: 6 }}>
      <span style={{
        fontSize: 12, fontWeight: 800, letterSpacing: '.09em',
        textTransform: 'uppercase', color: C.dim,
      }}>{children}</span>
      {note ? <span style={{ fontSize: 12, color: C.dim }}>{note}</span> : null}
    </div>
  );
}

/** One unfoldable row: chevron + key on line one, the value under it. */
function Row({ row, open, onToggle }) {
  return (
    <div data-session-row={row.key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={row.long ? open : undefined}
        aria-label={open ? `collapse ${row.key}` : `expand ${row.key}`}
        style={{
          display: 'flex', alignItems: 'baseline', gap: 6, width: '100%',
          font: 'inherit', fontFamily: MONO, fontSize: 15, textAlign: 'left',
          background: '#f7f7fa', border: `1px solid ${open ? C.line : 'transparent'}`,
          borderRadius: 6, padding: '5px 8px', cursor: row.long ? 'pointer' : 'default',
          color: row.group === 'flag' ? C.warn : C.text,
        }}
      >
        <span aria-hidden="true" style={{ flex: '0 0 auto', width: 14, color: C.dim }}>
          {row.long ? (open ? '▾' : '▸') : ''}
        </span>
        <span style={{ flex: '0 0 auto', fontWeight: 800 }}>{row.label}</span>
        <span style={{
          flex: 1, minWidth: 0, color: C.dim,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{row.preview}</span>
      </button>
      {row.long && open ? (
        <pre style={{
          margin: 0, padding: '6px 10px 8px 28px', fontFamily: MONO, fontSize: 15,
          lineHeight: 1.45, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{row.value}</pre>
      ) : null}
    </div>
  );
}

/**
 * A hidden instruction lane. The content IS the prompt as far as the model is
 * concerned and is NEVER on screen for the user — the studio is the only place
 * it can be read at all, so it is drawn beside the visible text rather than
 * behind a fold, and marked so the two can never be mistaken for each other.
 */
function HiddenBlock({ tag, text }) {
  return (
    <div data-session-hidden={tag} style={{
      borderLeft: `3px solid ${C.hidden}`, background: '#f6f2ff',
      borderRadius: '0 8px 8px 0', padding: '6px 9px',
      display: 'flex', flexDirection: 'column', gap: 3,
    }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: C.hidden, fontFamily: MONO }}>
        {`<${tag}>`} · hidden from the user, sent to the model
      </span>
      <span style={{
        fontSize: 14, lineHeight: 1.45, color: C.text, fontFamily: MONO,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>{text}</span>
    </div>
  );
}

/** One ordered photo: its thumbnail (or why there is none) under its role. */
function Thumb({ item }) {
  const box = {
    width: 74, height: 74, borderRadius: 8, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f2f2f6', border: `1px solid ${C.line}`,
  };
  return (
    <div data-session-image={item.index} style={{ width: 74, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {item.url && isRenderableSrc(item.url) ? (
        <img src={item.url} alt={item.role || `photo ${item.index}`} style={{ ...box, objectFit: 'cover' }} />
      ) : (
        <div style={{ ...box, border: `1px dashed ${C.line}`, padding: 4 }}>
          <span style={{
            fontSize: 11, fontFamily: MONO, color: C.dim, textAlign: 'center',
            overflow: 'hidden', wordBreak: 'break-all', lineHeight: 1.25,
          }}>{item.url ? item.url.slice(0, 34) : 'not picked'}</span>
        </div>
      )}
      <span style={{ fontSize: 12, color: C.text, lineHeight: 1.25 }}>
        <b>{item.index}</b>{item.role ? ` · ${item.role}` : ''}
      </span>
      {item.title ? (
        <span style={{ fontSize: 11, color: C.dim, lineHeight: 1.25 }}>{item.title}</span>
      ) : null}
    </div>
  );
}

export default function SessionCard({ node }) {
  const [pick, setPick] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  // The reader's own click in the running app re-seeds the card: the switcher
  // is an inspection, never a second selection that outlives the real one.
  useEffect(() => { setPick(null); }, [node?.activeKey]);

  const key = pick ?? node?.activeKey ?? null;
  const { view, ground } = useMemo(
    () => (node ? node.rowsFor(key) : { view: null, ground: null }),
    [node, key],
  );
  // The group heading is drawn on the FIRST row of each run, so the tail stays
  // one flat scroller (a nested list per group would give the card a second
  // scroll axis) while still saying which question each run answers.
  const tail = useMemo(() => (view?.rest || []).map((r, i, all) => ({
    ...r,
    head: i === 0 || all[i - 1].group !== r.group ? r.group : null,
  })), [view]);
  const toggle = (k) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  if (!node) return null;
  const options = node.options || [];
  const { template, prompt, injections, images } = view || {};
  // The whole prompt is one unresolved reference: this page was not opened on
  // the row (or the args) the prompt is read from, which is a different thing
  // from a prompt that is empty — and the only one a reader can act on.
  const unresolvedPrompt = isUnresolved(prompt?.visible);

  return (
    <div
      data-testid="session-card"
      data-session-kind={node.kind}
      data-session-type={node.sessionType}
      style={{
        alignSelf: 'stretch', flex: '1 1 auto', minHeight: 0,
        display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left',
        background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, padding: 10,
      }}
    >
      {/* ── the header: which session, and does it start here ─────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
        <span style={{
          fontSize: 12, fontWeight: 800, letterSpacing: '.09em',
          textTransform: 'uppercase', color: C.dim,
        }}>
          {node.startsHere ? 'starts a session' : 'carries the session preflight'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span
            data-testid="session-type"
            style={{
              fontSize: 20, fontWeight: 800, fontFamily: MONO, letterSpacing: '-.01em',
              color: '#fff', background: C.accent, borderRadius: 8, padding: '2px 9px',
            }}
          >{node.sessionType}</span>
          <span style={{ fontSize: 14, color: C.dim }}>session_type</span>
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{KIND_LABEL[node.kind]}</span>
        <span data-testid="session-ladder" style={{ fontSize: 14, color: C.text, lineHeight: 1.4 }}>
          {(node.ladder || []).join(' → ')}
        </span>
        {template && (template.name || (template.id && !isUnresolved(template.id))) ? (
          <div data-testid="session-template" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {template.cover ? (
              <img
                src={template.cover}
                alt=""
                style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'cover', border: `1px solid ${C.line}` }}
              />
            ) : null}
            <span style={{ fontSize: 14, color: C.text, minWidth: 0 }}>
              {template.name ? <b>{template.name}</b> : null}
              {template.id ? <code style={{ color: C.dim, marginLeft: template.name ? 6 : 0 }}>{template.id}</code> : null}
            </span>
          </div>
        ) : null}
        {ground ? (
          <span
            data-testid="session-ground"
            title={ground.text}
            style={{
              fontSize: 13, color: C.dim, lineHeight: 1.4,
              // TWO LINES AT MOST. The header is what stays put while the body
              // scrolls; a ground line that wraps to six eats the card.
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', wordBreak: 'break-word',
            }}
          >{ground.text}</span>
        ) : null}
        {node.boundOptions ? (
          <span style={{ fontSize: 13, color: C.dim }}>
            options ← <code>{node.boundOptions}</code> — the feed supplies them
          </span>
        ) : null}
      </div>

      {options.length ? (
        <div style={{ flex: '0 0 auto', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {options.map((o) => {
            const on = String(o.key) === String(key);
            return (
              <button
                key={o.key}
                type="button"
                data-session-option={o.key}
                data-selected={on ? '1' : undefined}
                onClick={() => setPick(o.key)}
                style={{
                  font: 'inherit', fontSize: 14, lineHeight: 1.2, padding: '3px 7px',
                  borderRadius: 999, cursor: 'pointer',
                  border: `1px solid ${on ? C.accent : C.line}`,
                  background: on ? C.accent : '#fff', color: on ? '#fff' : C.dim,
                }}
              >{o.label}</button>
            );
          })}
          {node.untouched ? (
            <span style={{ fontSize: 13, color: C.dim, alignSelf: 'center' }}>
              nothing clicked yet — showing the control’s own default
            </span>
          ) : null}
        </div>
      ) : null}

      {/* ── the body: prompt · injections · images · everything else ──────── */}
      <div
        data-vi-scroll="1"
        data-testid="session-rows"
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}
      >
        <Heading note={prompt ? 'what the model reads' : null}>prompt</Heading>
        {prompt ? (
          <div data-testid="session-prompt" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {prompt.visible ? (
              <div style={{
                fontSize: 15, lineHeight: 1.5, background: '#f7f7fa',
                border: `1px solid ${C.line}`, borderRadius: 8, padding: '7px 9px',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                // an UNRESOLVED prompt is a reference, not a prompt — it must
                // never read as though the model is being sent `⟨…⟩`
                color: unresolvedPrompt ? C.warn : C.text,
                fontFamily: unresolvedPrompt ? MONO : 'inherit',
              }}>{prompt.visible}</div>
            ) : (
              <span style={{ fontSize: 14, color: C.dim }}>
                nothing visible — this prompt is hidden instructions only
              </span>
            )}
            {unresolvedPrompt ? (
              <span style={{ fontSize: 13, color: C.dim, lineHeight: 1.4 }}>
                not resolved here — this page carries no such value yet. Open it the
                way the app does (tap a template in the preview) and the real prompt lands.
              </span>
            ) : null}
            {prompt.prefix ? <HiddenBlock tag="vi_prefix" text={prompt.prefix} /> : null}
            {prompt.suffix ? <HiddenBlock tag="vi_suffix" text={prompt.suffix} /> : null}
            {prompt.tags.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'baseline' }}>
                <span style={{ fontSize: 13, color: C.dim }}>widget tags</span>
                {prompt.tags.map((t) => (
                  <code key={t} style={{
                    fontSize: 13, color: C.good, background: '#eef8f2',
                    border: '1px solid #cfe9dc', borderRadius: 999, padding: '1px 7px',
                  }}>{t}</code>
                ))}
              </div>
            ) : null}
            {prompt.tokens.length ? (
              <span style={{ fontSize: 13, color: C.warn, fontFamily: MONO }}>
                ⚠ unfilled tokens · {prompt.tokens.join(' ')}
              </span>
            ) : null}
          </div>
        ) : (
          <span style={{ fontSize: 14, color: C.dim }}>
            no prompt — the session opens on whatever the sheet collects
          </span>
        )}

        <Heading note="what steers it">injections</Heading>
        {injections?.length ? (
          <div data-testid="session-injections" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {injections.map((inj) => (
              <div
                key={inj.key}
                data-session-injection={inj.key}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 1,
                  background: '#f7f7fa', borderRadius: 6, padding: '4px 8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <code style={{ fontSize: 14, fontWeight: 800, color: C.text, flex: '0 0 auto' }}>{inj.key}</code>
                  <span style={{
                    fontSize: 14, fontFamily: MONO, minWidth: 0, flex: 1,
                    color: inj.missing ? C.warn : C.text, wordBreak: 'break-word',
                  }}>{inj.value}</span>
                </div>
                <span style={{ fontSize: 12, color: C.dim, lineHeight: 1.3 }}>{inj.hint}</span>
              </div>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 14, color: C.dim }}>
            none — this session carries no template or policy switches
          </span>
        )}

        <Heading note={images?.items.length ? `${images.picked} of ${images.items.length} picked` : null}>
          images
        </Heading>
        {images?.items.length ? (
          <>
            <div data-testid="session-images" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {images.items.map((it) => <Thumb key={it.index} item={it} />)}
            </div>
            {images.picked === 0 ? (
              <span style={{ fontSize: 13, color: C.dim, lineHeight: 1.4 }}>
                {node.startsHere
                  ? 'none picked yet — the capture sheet collects them in this order'
                  : 'none picked yet — the upload page collects them in this order, then dispatches'}
              </span>
            ) : null}
          </>
        ) : (
          <span data-testid="session-images-empty" style={{ fontSize: 14, color: C.dim, lineHeight: 1.4 }}>
            {images?.pending
              ? <>none here — <code>{images.pending}</code> is filled by the upload form when the button is tapped</>
              : 'no photos travel with this payload — the capture sheet collects them'}
          </span>
        )}

        {tail.length ? (
          <>
            <Heading note="nothing is dropped">also sent</Heading>
            {tail.map((r) => (
              <div key={r.key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {r.head ? (
                  <span style={{
                    fontSize: 12, fontWeight: 800, letterSpacing: '.09em',
                    textTransform: 'uppercase', color: C.dim, paddingTop: 4,
                  }}>{GROUP_LABEL[r.head] || r.head}</span>
                ) : null}
                <Row row={r} open={open.has(r.key)} onToggle={() => toggle(r.key)} />
              </div>
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
}
